import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import PageHead from '@/components/PageHead';
import { Building2, FileText, Phone, Mail, MapPin } from 'lucide-react';

const LEGAL = [
  ['Dénomination sociale', 'ALOFI SA'],
  ['Enseigne commerciale', 'Intermarché'],
  ['Forme juridique', 'Société par Actions Simplifiée (SAS)'],
  ['Capital social', '64 000,00 €'],
  ['Siège social', '92 Allée de la Picholine, Quartier Pérouse Ouest, 30320 Marguerittes, France'],
  ['SIREN', '383 370 442'],
  ['SIRET (siège)', '383 370 442 00014'],
  ['Numéro de TVA intracommunautaire', 'FR89 383 370 442'],
  ['RCS', 'Nimes — inscrit le 21/10/1991'],
  ['Code APE / NAF', '47.11D — Supermarchés'],
  ['Activité principale', 'Exploitation d’un fonds de commerce de distribution à dominante alimentaire'],
  ['Convention collective', 'Commerce de détail et de gros à prédominance alimentaire — IDCC 2216'],
  ['Président', 'Monsieur Romain Vicario'],
  ['Commissaire aux comptes', 'ENDRIX PRO (SIREN 712 621 390)'],
  ['Date de création', '21 octobre 1991'],
];

export default function MentionsLegalesPage() {
  return (
    <div data-testid="mentions-legales-page">
      <PageHead title="Mentions légales" description="Mentions légales ALOFI SA — SIREN 383 370 442, RCS Nîmes, capital 64 000 €." />
      <section className="hero-bg noise-overlay relative">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          <Badge variant="outline" className="tracking-[0.08em] uppercase text-[10px] bg-white/80">Informations légales</Badge>
          <h1 className="mt-3 font-heading text-3xl sm:text-5xl font-semibold tracking-tight">Mentions légales</h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">Conformément aux dispositions des articles 6-III et 19 de la Loi n° 2004-575 du 21 juin 2004 pour la Confiance dans l’économie numérique (LCEN), nous portons à la connaissance des utilisateurs et visiteurs du site les informations suivantes.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-8">
        <Card className="lg:col-span-2 rounded-2xl border-border p-7">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[hsl(186_45%_92%)] flex items-center justify-center"><Building2 className="w-5 h-5 text-[hsl(186_55%_38%)]" /></div>
            <h2 className="font-heading text-xl font-semibold">Éditeur du site</h2>
          </div>
          <Separator className="my-4" />
          <dl className="divide-y divide-border">
            {LEGAL.map(([k, v]) => (
              <div key={k} className="py-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid={`legal-${k.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                <dt className="text-xs uppercase tracking-[0.08em] text-muted-foreground sm:col-span-1">{k}</dt>
                <dd className="text-sm sm:col-span-2">{v}</dd>
              </div>
            ))}
          </dl>
          <Separator className="my-6" />
          <h3 className="font-heading font-semibold">Contact</h3>
          <ul className="mt-3 space-y-2 text-sm">
            <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-[hsl(186_55%_38%)]" /><a href="tel:0537078109">05 37 07 81 09</a> · Fax : 05 37 07 99 90</li>
            <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-[hsl(186_55%_38%)]" /><a href="mailto:contact@alofisa.eu">contact@alofisa.eu</a></li>
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[hsl(186_55%_38%)]" />92 Allée de la Picholine, Quartier Pérouse Ouest, 30320 Marguerittes, France</li>
          </ul>
          <Separator className="my-6" />
          <h3 className="font-heading font-semibold">Directeur de la publication</h3>
          <p className="text-sm mt-2">Monsieur Romain Vicario, en qualité de Président de ALOFI SA.</p>
          <Separator className="my-6" />
          <h3 className="font-heading font-semibold">Hébergement</h3>
          <p className="text-sm mt-2">Le site est hébergé par <strong>IONOS SARL</strong>, 7 place de la Gare, 57200 Sarreguemines, France — tél. : <a href="tel:0970808911">0970 808 911</a> — site : <a href="https://www.ionos.fr" target="_blank" rel="noreferrer" className="underline">ionos.fr</a>.</p>
          <Separator className="my-6" />
          <h3 className="font-heading font-semibold">Propriété intellectuelle</h3>
          <p className="text-sm mt-2 leading-relaxed">L’ensemble des contenus présents sur le site (textes, images, logos, marques, charte graphique) sont la propriété exclusive de ALOFI SA ou de leurs ayants droit. Toute reproduction, représentation, modification, publication, transmission, dénaturation, totale ou partielle, par quelque procédé que ce soit et sur quelque support que ce soit, est interdite sans autorisation écrite préalable de ALOFI SA.</p>
          <Separator className="my-6" />
          <h3 className="font-heading font-semibold">Limitation de responsabilité</h3>
          <p className="text-sm mt-2 leading-relaxed">ALOFI SA s’efforce de fournir sur le site des informations aussi précises que possible. Toutefois, elle ne pourra être tenue responsable des omissions, des inexactitudes et des carences dans la mise à jour. Les informations indiquées sur le site ne sont pas exhaustives et ne constituent pas une offre commerciale ; les prix et disponibilités sont communiqués sur devis.</p>
          <Separator className="my-6" />
          <h3 className="font-heading font-semibold">Droit applicable</h3>
          <p className="text-sm mt-2 leading-relaxed">Tout litige en relation avec l’utilisation du site alofisa.eu est soumis au droit français. À défaut de résolution amiable, compétence exclusive est attribuée aux tribunaux du ressort de Nimes.</p>
        </Card>

        <div className="space-y-6">
          <Card className="rounded-2xl border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[hsl(186_45%_92%)] flex items-center justify-center"><FileText className="w-5 h-5 text-[hsl(186_55%_38%)]" /></div>
              <h3 className="font-heading font-semibold">Documents</h3>
            </div>
            <Separator className="my-4" />
            <ul className="space-y-2 text-sm">
              <li><Link to="/confidentialite" className="hover:text-[hsl(186_55%_38%)]">Politique de confidentialité</Link></li>
              <li><Link to="/cgv" className="hover:text-[hsl(186_55%_38%)]">Conditions générales</Link></li>
              <li><Link to="/contact" className="hover:text-[hsl(186_55%_38%)]">Nous contacter</Link></li>
            </ul>
          </Card>
          <Card className="rounded-2xl border-border p-6 bg-secondary/40">
            <h3 className="font-heading font-semibold">Mise à jour</h3>
            <p className="text-xs text-muted-foreground mt-2">Dernière mise à jour : 29/04/2026. Source des informations juridiques : Pappers · RCS Nimes · INPI.</p>
          </Card>
        </div>
      </section>
    </div>
  );
}
